import React, { useCallback, useEffect, useState } from 'react';
import {
  UnbilledItem,
  createInvoice,
  getUnbilledItems,
} from '../services/invoiceService';
import { createPackageInvoice } from '../services/paymentService';
import DatePickerInput from './DatePickerInput';

interface Props {
  clients: { client_id: string; client_name: string }[];
  onClose: () => void;
  onCreated: () => void;
}

type ItemType = 'all' | 'session' | 'package_payment';

const TYPE_BADGE: Record<string, string> = {
  session:         'bg-blue-900/40 text-blue-300 border-blue-800/50',
  package_payment: 'bg-purple-900/40 text-purple-300 border-purple-800/50',
};
const TYPE_LABEL: Record<string, string> = {
  session:         'Session',
  package_payment: 'Package',
};

const CreateInvoiceModal: React.FC<Props> = ({ clients, onClose, onCreated }) => {
  const [items, setItems]             = useState<UnbilledItem[]>([]);
  const [loading, setLoading]         = useState(true);
  const [fetchError, setFetchError]   = useState('');

  const [clientFilter, setClientFilter]     = useState('');
  const [dateFrom, setDateFrom]             = useState('');
  const [dateTo, setDateTo]                 = useState('');
  const [typeFilter, setTypeFilter]         = useState<ItemType>('all');

  const [selected, setSelected]     = useState<Set<string>>(new Set());
  const [creating, setCreating]     = useState(false);
  const [progress, setProgress]     = useState<{ done: number; total: number } | null>(null);
  const [errors, setErrors]         = useState<{ id: string; desc: string; msg: string }[]>([]);
  const [done, setDone]             = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    setSelected(new Set());
    try {
      const data = await getUnbilledItems({
        client_id: clientFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        item_type: typeFilter === 'all' ? undefined : typeFilter,
      });
      setItems(data);
    } catch (e: any) {
      setFetchError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [clientFilter, dateFrom, dateTo, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const allIds = items.map(i => i.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
  };

  const toggleOne = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    const toCreate = items.filter(i => selected.has(i.id));
    if (!toCreate.length) return;

    setCreating(true);
    setErrors([]);
    setProgress({ done: 0, total: toCreate.length });

    const errs: { id: string; desc: string; msg: string }[] = [];

    for (let idx = 0; idx < toCreate.length; idx++) {
      const item = toCreate[idx];
      try {
        if (item.item_type === 'session') {
          await createInvoice({ appointment_id: item.id });
        } else {
          if (!item.enrollment_id) throw new Error('Missing enrollment ID');
          await createPackageInvoice(item.enrollment_id, item.id);
        }
      } catch (e: any) {
        errs.push({ id: item.id, desc: item.description, msg: e.message || 'Failed' });
      }
      setProgress({ done: idx + 1, total: toCreate.length });
    }

    setErrors(errs);
    setCreating(false);
    setDone(true);
    if (errs.length < toCreate.length) {
      onCreated();
    }
  };

  const selectedCount = selected.size;
  const fmt = (cents: number, currency: string) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'usd' }).format(cents / 100);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-dark-card border border-dark-border rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-white">Create Invoices</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors text-xl leading-none">&times;</button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-dark-border flex-shrink-0">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">Client</label>
              <select
                value={clientFilter}
                onChange={e => setClientFilter(e.target.value)}
                className="px-3 py-1.5 text-sm bg-dark-canvas border border-dark-border rounded-lg text-gray-200 focus:outline-none focus:border-primary-500"
              >
                <option value="">All clients</option>
                {clients.map(c => (
                  <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">Date From</label>
              <DatePickerInput
                value={dateFrom}
                onChange={setDateFrom}
                placeholder="Start date"
                maxDate={dateTo ? new Date(dateTo + 'T00:00:00') : undefined}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">Date To</label>
              <DatePickerInput
                value={dateTo}
                onChange={setDateTo}
                placeholder="End date"
                minDate={dateFrom ? new Date(dateFrom + 'T00:00:00') : undefined}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">Type</label>
              <div className="flex rounded-lg border border-dark-border overflow-hidden text-sm">
                {(['all', 'session', 'package_payment'] as ItemType[]).map(t => (
                  <button
                    key={t}
                    onClick={() => setTypeFilter(t)}
                    className={`px-3 py-1.5 transition-colors ${
                      typeFilter === t
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-dark-hover'
                    }`}
                  >
                    {t === 'all' ? 'All' : t === 'session' ? 'Sessions' : 'Packages'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {fetchError && <p className="text-sm text-red-400 mb-3">{fetchError}</p>}
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500">No unbilled items match the current filters.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-dark-border">
                  <th className="pb-2 pr-3 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="rounded border-dark-border accent-primary-500"
                    />
                  </th>
                  <th className="pb-2 pr-4 text-left font-medium">Type</th>
                  <th className="pb-2 pr-4 text-left font-medium">Client</th>
                  <th className="pb-2 pr-4 text-left font-medium">Description</th>
                  <th className="pb-2 pr-4 text-left font-medium">Date</th>
                  <th className="pb-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {items.map(item => {
                  const isChecked = selected.has(item.id);
                  const dateStr = item.date
                    ? new Date(item.date + (item.item_type === 'session' ? '' : 'T00:00:00'))
                        .toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—';
                  return (
                    <tr
                      key={item.id}
                      className={`text-gray-300 hover:bg-dark-hover/30 cursor-pointer ${isChecked ? 'bg-primary-900/10' : ''}`}
                      onClick={() => toggleOne(item.id)}
                    >
                      <td className="py-2.5 pr-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleOne(item.id)}
                          className="rounded border-dark-border accent-primary-500"
                        />
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${TYPE_BADGE[item.item_type]}`}>
                          {TYPE_LABEL[item.item_type]}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 font-medium">{item.client_name}</td>
                      <td className="py-2.5 pr-4 text-gray-400 text-xs">{item.description}</td>
                      <td className="py-2.5 pr-4 text-gray-400 whitespace-nowrap">{dateStr}</td>
                      <td className="py-2.5 text-right font-medium">{fmt(item.amount_cents, item.currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Post-create errors */}
          {done && errors.length > 0 && (
            <div className="mt-4 p-3 bg-red-900/20 border border-red-800/40 rounded-lg">
              <p className="text-xs font-semibold text-red-300 mb-2">
                {errors.length} invoice{errors.length > 1 ? 's' : ''} failed:
              </p>
              <ul className="space-y-1">
                {errors.map(e => (
                  <li key={e.id} className="text-xs text-red-400">
                    <span className="text-gray-400">{e.desc}</span> — {e.msg}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-border flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-gray-500">
            {selectedCount > 0
              ? `${selectedCount} item${selectedCount > 1 ? 's' : ''} selected`
              : 'Select items to invoice'}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white border border-dark-border rounded-lg transition-colors"
            >
              {done ? 'Close' : 'Cancel'}
            </button>
            {!done && (
              <button
                onClick={handleCreate}
                disabled={selectedCount === 0 || creating}
                className="px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
              >
                {creating && progress
                  ? `Creating… ${progress.done}/${progress.total}`
                  : `Create Draft${selectedCount > 1 ? 's' : ''}${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;
